import { useAuth0 } from '@auth0/auth0-react';
import { XCircle, LogOut, Mail, User } from 'lucide-react';
import type { Admin } from '../hooks/useAdminStatus';

interface AccessDeniedProps {
  admin: Admin;
}

export function AccessDenied({ admin }: AccessDeniedProps) {
  const { logout } = useAuth0();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-10 blur-3xl bg-gradient-to-br from-red-600 to-red-700"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full opacity-10 blur-3xl bg-gradient-to-br from-red-700 to-red-800"></div>
      </div>

      <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-red-500/20 max-w-md w-full text-center relative z-10 shadow-2xl">
        <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
          <XCircle className="w-10 h-10 text-white" />
        </div>
        
        <h1 className="text-3xl font-bold bg-gradient-to-r from-white via-red-100 to-red-200 bg-clip-text text-transparent mb-2">
          Access Denied
        </h1>
        <p className="text-slate-400 mb-8">
          Your admin account request has been denied
        </p>

        {/* User Details */}
        <div className="bg-white/5 rounded-xl p-6 mb-6 border border-slate-700/30">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-400">Name</span>
              </div>
              <span className="text-sm text-white font-medium">
                {admin.firstname} {admin.lastname}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-400">Email</span>
              </div>
              <span className="text-sm text-white font-medium">
                {admin.email}
              </span>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div className="inline-flex items-center space-x-2 bg-red-500/20 text-red-400 px-4 py-2 rounded-full border border-red-500/30 mb-6">
          <XCircle className="w-4 h-4" />
          <span className="text-sm font-medium">ACCESS DENIED</span>
        </div>

        <div className="space-y-4">
          <p className="text-slate-300 text-sm">
            Your request for admin access has been reviewed and denied. 
            You do not have permission to access this system.
          </p>
          
          <p className="text-xs text-slate-500">
            If you believe this is an error, please contact your system administrator.
          </p>
        </div>

        <button
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="w-full flex items-center justify-center space-x-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl font-medium mt-8 group"
        >
          <LogOut className="w-5 h-5 group-hover:scale-110 transition-transform" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
}
